import React, { useState, useRef, useEffect } from "react";
import { Progress, Button, Popconfirm, Input, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import editIcon from "../../../Assets/Icons/editTable.svg";
import "./style.css";
import { editRiskFactor, getRiskFactors } from "../../../API/User/riskfactor";
import { editRiskRanges, getRiskRange } from "../../../API/User/riskpoints";
import { getAverageScore } from "../../../API/User/averageprobabiltyscore";

const ProbabilityMeter = () => {
  const [averageScore, setAverageScore] = useState(0);
  const [totalScoreSum, setTotalScoreSum] = useState(0);
  const [editingRow, setEditingRow] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [hoverRow, setHoverRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const [data, setRiskData] = useState([]);
  const [ranges, setRanges] = useState([]);

  const errorNotification = (msg) => {
    messageApi.error(msg);
  };

  useEffect(() => {
    const fetchScore = async () => {
      try {
        const res = await getAverageScore();

        if (Math.floor(res.status / 100) === 2) {
          const avgScore = res?.data?.average_score ?? 0;
          const totalScore = res?.data?.total_score_sum ?? 200;

          setAverageScore(avgScore);
          setTotalScoreSum(totalScore);
        } else {
          messageApi.error("Failed to fetch average score.");
        }
      } catch (error) {
        console.error("Error fetching average score:", error);
        messageApi.error("Failed to fetch average score.");
      }
    };

    fetchScore();
  }, []);

  const getRiskDetails = () => {
    if (!ranges.length) return { label: "", color: "#d9d9d9" };

    // Find the range that matches the actual averageScore
    const range = ranges.find(
      (r) => averageScore >= r.start_range && averageScore <= r.end_range,
    );

    if (!range) return { label: "", color: "#d9d9d9" };

    // Map colors to range names
    const colorMap = {
      Low: "#1FC16B",
      Moderate: "#FFBF00",
      High: "#E53935",
      "Very High": "#B71C1C",
    };

    // Fixed percentages for ranges
    const percentMap = {
      Low: 25,
      Moderate: 50,
      High: 75,
      "Very High": 100,
    };

    return {
      label: range.name,
      color: colorMap[range.name] || "#d9d9d9",
      percent: percentMap[range.name] || 0,
    };
  };

  const handleEdit = (index) => {
    setEditingRow(index);
    setEditingValue(data[index].points);
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditingValue(null);
  };

  const handleSavePoints = async (index) => {
    if (editingValue === null || editingValue === "") {
      return errorNotification("Please enter a valid points value");
    }

    setLoading(true);
    try {
      const factor = data[index];
      const res = await editRiskFactor(factor.id, Number(editingValue));

      if (Math.floor(res.status / 100) === 2) {
        const newData = [...data];
        newData[index].points = Number(editingValue);
        setRiskData(newData);

        messageApi.success("Points updated successfully!");
        setEditingRow(null);
        setEditingValue(null);
      } else {
        errorNotification("Failed to update points.");
      }
    } catch (err) {
      console.error(err);
      errorNotification("Error updating points.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRanges = async () => {
    try {
      setLoading(true);

      const cleanedPayload = ranges.map((r) => {
        if (r.name === "Very High") {
          return {
            id: r.id,
            name: r.name,
            start_range: r.start_range,
          };
        }
        return r;
      });

      const res = await editRiskRanges(cleanedPayload);
      if (Math.floor(res.status / 100) === 2) {
        messageApi.success("Ranges updated successfully!");
        setIsEditing(false);
      } else errorNotification("Failed to update ranges.");
    } catch (err) {
      const backendMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Error updating ranges.";

      errorNotification(backendMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getRiskFactors();

      if (Math.floor(response?.status / 100) === 2) {
        const raw = response?.data;
        const arr = Array.isArray(raw) ? raw : raw?.risk_factors || [];

        setRiskData(arr);
      } else {
        messageApi.error("Unexpected response from server.");
      }
    } catch (err) {
      messageApi.error("Failed to fetch risk factors");
    } finally {
      setLoading(false);
    }
  };

  const fetchRiskRanges = async () => {
    try {
      const res = await getRiskRange();

      if (Math.floor(res.status / 100) === 2) {
        const apiRanges = res.data.risk_points.map((r) => {
          return {
            id: r.id,
            name: r.name,
            start_range: r.start_range,
            end_range: r.end_range,
          };
        });
        setRanges(apiRanges);
      } else {
        errorNotification("Failed to fetch risk ranges.");
      }
    } catch (err) {
      errorNotification("Failed to fetch risk ranges.");
    }
  };

  useEffect(() => {
    fetchData();
    fetchRiskRanges();
  }, []);

  const handleEditToggle = () => {
    if (isEditing) {
      handleSaveRanges();
    } else {
      setIsEditing(true);
    }
  };
  const handleRangeChange = (index, key, value) => {
    const newRanges = [...ranges];

    if (key === "start") {
      newRanges[index].start_range = Number(value);
    }

    if (key === "end") {
      newRanges[index].end_range = Number(value);
    }

    setRanges(newRanges);
  };

  return (
    <div className="probability-meter">
      {contextHolder}
      <div className="status-wrapper">
        <div className="score-title">Theft Probability Score</div>
        <div className="score-text">
          {averageScore} / {totalScoreSum}
        </div>
      </div>

      <div className="progress">
        <Progress
          strokeColor={getRiskDetails().color}
          percent={getRiskDetails().percent}
          showInfo={false}
        />
        <div className="label-data" style={{ color: getRiskDetails().color }}>
          {getRiskDetails().label}
        </div>

        <div className="description">
          The Theft Probability Meter visually represents the total theft score,
          with color-coded ranges indicating the risk level: Very High, High,
          Moderate, Low.
        </div>

        <div className="ranges-row">
          {ranges.map((range, index) => {
            const isFirst = index === 0;
            const isLast = index === ranges.length - 1;

            return (
              <div className="range-box" key={index}>
                <span className="label">{range.name}</span>

                <div className="value-wrapper">
                  {isEditing ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {/* Prefix indicators */}
                      {isFirst && <span>&lt; </span>}
                      {isLast && <span>&gt; </span>}

                      <Input
                        type="number"
                        value={isFirst ? range.end_range : range.start_range}
                        onChange={(e) =>
                          handleRangeChange(
                            index,
                            isFirst ? "end" : "start",
                            e.target.value,
                          )
                        }
                        style={{ width: "70px", margin: "0 5px" }}
                      />

                      {/* Range separator and second input for middle values (Moderate/High) */}
                      {!isFirst && !isLast && (
                        <>
                          -
                          <Input
                            type="number"
                            value={range.end_range}
                            onChange={(e) =>
                              handleRangeChange(index, "end", e.target.value)
                            }
                            style={{ width: "70px", marginLeft: "5px" }}
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    <span className="value">
                      {isFirst && `< ${range.end_range}`}
                      {isLast && `> ${range.start_range}`}
                      {!isFirst &&
                        !isLast &&
                        `${range.start_range} - ${range.end_range}`}
                    </span>
                  )}

                  {!isEditing && (
                    <span
                      className={`circle ${
                        range.name
                          ? range.name.toLowerCase().replace(/\s+/g, "-")
                          : ""
                      }`}
                    />
                  )}
                </div>
              </div>
            );
          })}

          <Button
            type="primary"
            className="save-btn"
            onClick={handleEditToggle}
            style={{ marginLeft: "10px" }}
          >
            {isEditing ? "Save" : "Edit"}
          </Button>
        </div>
        <div className="factors-table">
          <div className="table-header">
            <span className="header-factor">Factors</span>
            <span className="header-points">Points</span>
          </div>

          {data.map((item, index) => (
            <div
              key={index}
              className={`table-row ${activeRow === index ? "selected" : ""}`}
              onMouseEnter={() => setHoverRow(index)}
              onMouseLeave={() => setHoverRow(null)}
            >
              <span className="factor">{item.name}</span>

              <span className="points">
                {editingRow === index ? (
                  <Input
                    type="number"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    style={{ width: "60px" }}
                  />
                ) : (
                  item.points
                )}
              </span>

              {hoverRow === index && (
                <div style={{ display: "flex", gap: "13px" }}>
                  {editingRow === index ? (
                    <>
                      <Button
                        className="save-edit"
                        type="primary"
                        size="small"
                        onClick={() => handleSavePoints(index)}
                      >
                        Save Changes
                      </Button>
                      <Button
                        className="cancel-edit "
                        size="small"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(index);
                      }}
                    >
                      <img src={editIcon} alt="Edit" />
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProbabilityMeter;
